import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { MaxBrainAuthState } from 'app/projects/auth/src/lib/models/auth.state';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { EntityService } from 'app/projects/entity/src/lib/services/entity.service';
import { BehaviorSubject } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { JobStatus } from '../library/shared/async-job-drawer/enums/job-status.enum';
import { IJobQuery } from '../library/shared/async-job-drawer/interfaces/job.query.interface';
import { IJobQueryPaginated } from '../library/shared/async-job-drawer/interfaces/jobs-paginated.query.interface';
import { IMessagePaginated } from '../library/shared/async-job-drawer/interfaces/message-paginated.query.interface';
import { JobCommand } from '../library/shared/async-job-drawer/models/job.comand.model';
import { Job } from '../library/shared/async-job-drawer/models/job.model';
import { Message } from '../library/shared/async-job-drawer/models/message.model';
import { TenantDataService } from 'app/config/services/tenant-data.service';

export interface Pagination {
    numberOfPages: number;
    numberOfResults: number;
}

@Injectable()
export class AsyncJobApiService extends EntityService<IJobQuery> {
    private _asyncJobs = new BehaviorSubject<Job[]>([]);
    public asyncJobs$ = this._asyncJobs.asObservable();

    jobStatus: BehaviorSubject<Job> = new BehaviorSubject<Job>(null);

    jobAdded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    jobFinished = new BehaviorSubject<{ jobId: string; nodeId: string }>(null);
    jobsPaginationChanged: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    jobsPage = 1;
    messagesPage = 1;
    numberOfActiveAsyncJobs = 0;

    get asyncJobs(): Job[] {
        return this._asyncJobs.getValue();
    }

    private _completedJobs: number = null;
    private _updated = false;

    private _jobsPagination: Pagination = { numberOfPages: 0, numberOfResults: 0 };
    private _messagesPagination: Pagination = { numberOfPages: 0, numberOfResults: 0 };

    get completedJobs(): number {
        return this._completedJobs;
    }
    set completedJobs(value: number) {
        this._completedJobs = value;
    }

    get updated(): boolean {
        return this._updated;
    }
    set updated(value: boolean) {
        this._updated = value;
    }

    get jobsPagination(): Pagination {
        return this._jobsPagination;
    }
    set jobsPagination(value: Pagination) {
        this._jobsPagination = value;
    }

    get messagesPagination(): Pagination {
        return this._messagesPagination;
    }
    set messagesPagination(value: Pagination) {
        this._messagesPagination = value;
    }

    accessToken: string;
    headers: {
        Authorization: string;
    };

    private async _getToken(): Promise<void> {
        this.accessToken = await this._store
            .select(MaxBrainAuthState.getAccessToken)
            .pipe(
                filter((x) => !!x),
                take(1)
            )
            .toPromise();
        this.headers = {
            Authorization: `Bearer ${this.accessToken}`,
        };
    }

    constructor(http: HttpClient, private _store: Store, @Inject(MAXBRAIN_ENVIRONMENT) environment: IEnvironment, private _tenantDataService: TenantDataService) {
        super(http, environment.apiUrl, 'certificate');
    }

    async getAllJobs(openDrawer = false, page = 1, checkActiveJobs = false): Promise<void> {
        await this._getToken();
        const url = this._tenantDataService.applyAccessTokenBackendUrl(`${this.apiUrl}/async-job/jobs?page=${page}`);
        this.jobsPage = page;
        this.http.get<IJobQueryPaginated>(url, { headers: this.headers }).subscribe((queries) => {
            this.updated = queries.updated;
            this.completedJobs = queries.completed;
            this.jobsPagination = {
                numberOfPages: queries.pagination.number_of_pages,
                numberOfResults: queries.pagination.number_of_results,
            };
            this.jobsPaginationChanged.next(true);
            const asyncJobs = queries.data.map((query) => new Job(query));

            // Check if any of job is in progress or created after login
            let activeJob = false;
            if (checkActiveJobs) {
                activeJob = asyncJobs.some((job) => job.communicatedStatus === JobStatus.InProgress || job.communicatedStatus === JobStatus.Created);
            }
            if (openDrawer || activeJob || (checkActiveJobs && this.updated)) {
                localStorage.setItem('isDrawerOpen', 'true');
                if (this.updated && checkActiveJobs) {
                    this.numberOfActiveAsyncJobs = 0;
                }
            }
            this._asyncJobs.next(asyncJobs);
        });
    }

    async getJobStatus(jobId: string): Promise<Job> {
        await this._getToken();
        const url = this._tenantDataService.applyAccessTokenBackendUrl(`${this.apiUrl}/async-job/jobs/${jobId}/status`);
        return this.http
            .get<IJobQuery>(url, { headers: this.headers })
            .pipe(map((query) => new Job(query)))
            .toPromise();
    }

    async getJobMessages(jobId: string, page = 1, pageSize = 5, messageStatus: string): Promise<Message[]> {
        await this._getToken();
        const url = this._tenantDataService.applyAccessTokenBackendUrl(`${this.apiUrl}/async-job/jobs/${jobId}/messages`);

        this.messagesPage = page;
        let params = new HttpParams().set('page', page.toString()).set('limit', pageSize.toString());

        if (messageStatus && messageStatus !== 'total') {
            params = params.set('status', messageStatus);
        }

        return this.http
            .get<IMessagePaginated>(url, {
                headers: this.headers,
                params: params,
            })
            .pipe(
                map((query) => {
                    this.messagesPagination = {
                        numberOfPages: query.pagination.number_of_pages,
                        numberOfResults: query.pagination.number_of_results,
                    };
                    return query.data.map((item) => new Message(item));
                })
            )
            .toPromise();
    }

    async markJobAsRead(jobId: string): Promise<boolean> {
        try {
            await this._getToken();
            const url = this._tenantDataService.applyAccessTokenBackendUrl(`${this.apiUrl}/async-job/jobs/mark-as-read`);
            const data = { async_jobs: [{ id: jobId }] };
            await this.http.patch<IJobQuery>(url, data, { headers: this.headers }).toPromise();
            return true;
        } catch (e) {
            console.warn(e);
        }
    }

    async cancelJob(entity: Job): Promise<Job> {
        try {
            await this._getToken();
            const url = this._tenantDataService.applyAccessTokenBackendUrl(`${this.apiUrl}/async-job/jobs/${entity.id}/cancel`);
            const data = new JobCommand(entity);
            return this.http
                .patch<IJobQuery>(url, data, { headers: this.headers })
                .pipe(map((query) => new Job(query)))
                .toPromise();
        } catch (e) {
            console.warn(e);
        }
    }

    async closeJob(entity: Job): Promise<Job> {
        try {
            await this._getToken();
            const url = this._tenantDataService.applyAccessTokenBackendUrl(`${this.apiUrl}/async-job/jobs/${entity.id}/close`);
            const data = new JobCommand(entity);
            return this.http
                .patch<IJobQuery>(url, data, { headers: this.headers })
                .pipe(map((query) => new Job(query)))
                .toPromise();
        } catch (e) {
            console.warn(e);
        }
    }

    /**
     * Opens the async jobs drawer (if needed), polls until the job ends, then refreshes the job list.
     * Use after bulk HTTP endpoints return 202 with a new job id.
     */
    trackEnqueuedJob(jobId: string, onTerminal?: () => void): void {
        void this._trackEnqueuedJob(jobId, onTerminal);
    }

    private async _trackEnqueuedJob(jobId: string, onTerminal?: () => void): Promise<void> {
        try {
            await this.getAllJobs(true);
            this.jobAdded.next(true);
            this.numberOfActiveAsyncJobs++;
            let jobStatus = await this.getJobStatus(jobId);

            if (jobStatus.communicatedStatus === JobStatus.Finished || jobStatus.communicatedStatus === JobStatus.TimedOut) {
                this.getAllJobs(Boolean(localStorage.getItem('isDrawerOpen')));
                this.completedJobs++;
                onTerminal?.();
            } else {
                const intervalId = setInterval(async () => {
                    jobStatus = await this.getJobStatus(jobId);
                    this.jobStatus.next(jobStatus);

                    if (
                        jobStatus.communicatedStatus === JobStatus.Finished ||
                        jobStatus.communicatedStatus === JobStatus.TimedOut ||
                        jobStatus.communicatedStatus === JobStatus.Canceled
                    ) {
                        this.getAllJobs(Boolean(localStorage.getItem('isDrawerOpen')));
                        this.completedJobs++;
                        onTerminal?.();
                        clearInterval(intervalId);
                    }
                }, 3000);
            }
        } catch (e) {
            console.warn(e);
        }
    }
}
